import { FormBase } from "../../util/form-base-form";
import { Roles } from "../../constants/roles";

class PostSharedPreviewForm extends FormBase {
    public getPreviewBody(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.PREVIEW_BODY}"]`, HTMLElement);
    }

    public getPreviewTitleField(root: ParentNode): HTMLInputElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.POST_TITLE}"], input[name="post_title"], textarea[name="post_title"]`,
            HTMLInputElement,
        );
    }

    public getPreviewTitle(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.PREVIEW_TITLE}"]`, HTMLElement);
    }

    public getContentCounter(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, "[data-post-content-counter]", HTMLElement);
    }

    public getPreviewContentCounter(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, "[data-post-content-counter-preview]", HTMLElement);
    }

    public getContentCountElement(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, "[data-post-content-count]", HTMLElement);
    }

    public getContentProgressFill(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, "[data-post-content-progress-fill]", HTMLElement);
    }

    public getContentThresholdBadge(root: ParentNode, threshold: string): HTMLElement | null {
        return this.queryElement(root, `[data-post-content-threshold="${threshold}"]`, HTMLElement);
    }

    public getReviewQuestionsPreview(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.REVIEW_QUESTIONS_PREVIEW}"]`, HTMLElement);
    }

    public getReviewQuestionElements(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, ".review-questions-section .review-question", HTMLElement);
    }

    public getReviewQuestionLabel(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.REVIEW_QUESTION_LABEL}"]`, HTMLElement);
    }

    public getRatingAnswerInput(root: ParentNode): HTMLInputElement | null {
        return this.getInput(root, `[data-role="${Roles.RATING_INPUT}"] [data-role="${Roles.REVIEW_ANSWER}"]`);
    }

    public getCheckedReviewRadio(root: ParentNode): HTMLInputElement | null {
        return this.getInput(root, 'input[type="radio"]:checked');
    }

    public getReviewOptionLabel(root: Element | Event | EventTarget | null | undefined): HTMLElement | null {
        return this.closest(root, `[data-role="${Roles.REVIEW_OPTION_LABEL}"], label`, HTMLElement);
    }

    public getReviewOptionText(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.REVIEW_OPTION_TEXT}"]`, HTMLElement);
    }

    public getSingleSelectAnswerInput(root: ParentNode): HTMLInputElement | null {
        return this.getInput(
            root,
            `[data-role="${Roles.REVIEW_SINGLE_SELECT}"] [data-role="${Roles.REVIEW_ANSWER}"][name^="review_data[answers]"]`,
        );
    }

    public getSingleSelectOptionText(root: ParentNode, optionIndex: string): HTMLElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.REVIEW_SINGLE_SELECT}"] [data-role="${Roles.REVIEW_OPTION_TEXT}"][data-option-index="${optionIndex}"]`,
            HTMLElement,
        );
    }

    public getSingleSelectElement(root: ParentNode): HTMLSelectElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.REVIEW_SINGLE_SELECT}"] select, select[data-review-multi-select="1"]`,
            HTMLSelectElement,
        );
    }

    public getMultiSelectAnswerInput(root: ParentNode): HTMLInputElement | null {
        return this.getInput(
            root,
            '.form-field[data-choices-bind-format="json"] input[type="hidden"][name^="review_data[answers]"]',
        );
    }

    public getMultiSelectElement(root: ParentNode): HTMLSelectElement | null {
        return this.queryElement(
            root,
            `select[data-review-multi-select="1"], [data-role="${Roles.CHOICES_FIELD}"] select[multiple]`,
            HTMLSelectElement,
        );
    }

    public getReviewAnswerTextarea(root: ParentNode): HTMLTextAreaElement | null {
        return this.queryElement(root, 'textarea[name^="review_data[answers]"]', HTMLTextAreaElement);
    }

    public getPreviewRichHost(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.PREVIEW_RICH_HOST}"]`, HTMLElement);
    }

    public getImagesPreviewGrid(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.IMAGES_PREVIEW_GRID}"]`, HTMLElement);
    }

    public getVideosPreviewGrid(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.VIDEOS_PREVIEW_GRID}"]`, HTMLElement);
    }

    public getSnsPreviewGrid(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.SNS_PREVIEW_GRID}"]`, HTMLElement);
    }

    public getImageManagerTiles(root: ParentNode): HTMLElement[] {
        return this.queryElements(
            root,
            `[data-role="${Roles.IMAGE_MANAGER}"] [data-role="${Roles.MEDIA_TILE}"]`,
            HTMLElement,
        );
    }

    public getMediaRemovedIndicator(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.MEDIA_REMOVED_INDICATOR}"]`, HTMLElement);
    }

    public getMediaControls(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.MEDIA_CONTROLS}"]`, HTMLElement);
    }

    public getMediaControlsList(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.MEDIA_CONTROLS}"]`, HTMLElement);
    }

    public getFormVideoGrid(root: ParentNode): HTMLElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.VIDEO_MANAGER}"] [data-role="${Roles.VIDEO_GRID}"]`,
            HTMLElement,
        );
    }

    public getFormSnsGrid(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.SNS_LIVE_PREVIEW}"]`, HTMLElement);
    }

    public getOgpPreviewSection(root: Element | Event | EventTarget | null | undefined): HTMLElement | null {
        return this.closest(root, `[data-role="${Roles.OGP_PREVIEW_SECTION}"]`, HTMLElement);
    }
}

const postSharedPreviewForm = new PostSharedPreviewForm();

export { PostSharedPreviewForm, postSharedPreviewForm };
